/*
  CasMarkt v2.0 item 3 — quick view.

  "Make the quick view more aesthetically pleasing; it takes a second to load,
  make it fast with smooth animations; have it pop up at the bottom right, it
  shouldn't take up a big part of the page."

  The element is still Spotlight's <quick-add-modal> extending ModalDialog and
  the content is still the product page's <product-info>, so <product-form>
  keeps posting to /cart/add and the cart drawer keeps opening itself. Two
  things change:

    1. The panel opens on the click, not after the network. cm-cards.js has
       usually already fetched the markup on hover; when it has, the HTML is
       written synchronously in the same task, so there is nothing to wait for.
       When it has not, a skeleton (photo block + three bars) paints instantly
       and the real content replaces it when it lands.
    2. On desktop this is a small docked panel, not a full-screen modal, so the
       page behind it keeps its scroll position and its scrollbar. Under 750px
       it is a bottom sheet and Spotlight's scroll lock is left alone.

  Everything else — geometry, motion, colour — is cm-cards.css §5.
*/
if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.openToken = 0;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          // Picking a variant can swap the whole <product-info>, which throws
          // away the panel's compact arrangement; connectedCallback re-fires
          // this event on the replacement, so rebuild it there too.
          this.compact();
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.openToken += 1;
        this.modalContent.innerHTML = '';
        this.classList.remove('cm-quickview--loading');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      /* Three grey bars and a photo block. Only ever seen on a cold open —
         a card that was clicked without being hovered first, or a slow
         connection. Same box as the real content so nothing jumps. */
      renderSkeleton() {
        this.classList.add('cm-quickview--loading');
        this.modalContent.innerHTML =
          '<div class="cm-qv-skel" aria-hidden="true">' +
          '<div class="cm-qv-skel__media"></div>' +
          '<div class="cm-qv-skel__bar cm-qv-skel__bar--sm"></div>' +
          '<div class="cm-qv-skel__bar"></div>' +
          '<div class="cm-qv-skel__bar cm-qv-skel__bar--md"></div>' +
          '<div class="cm-qv-skel__pill"></div>' +
          '</div>';
      }

      fill(html) {
        if (!html) return;
        const responseHTML = new DOMParser().parseFromString(html, 'text/html');
        const productElement = responseHTML.querySelector('product-info');
        if (!productElement) return;

        this.classList.remove('cm-quickview--loading');
        this.preprocessHTML(productElement);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();
        this.compact();
      }

      /* Two small rearrangements the CSS cannot do: "View full details" belongs
         under the title rather than at the far bottom of the product page's
         info column, and a product with several photos gets a thumbnail row
         instead of Spotlight's full media gallery, which does not fit — or
         behave — inside a 420px panel. */
      compact() {
        const info = this.modalContent.querySelector('.product__info-container');
        const title = this.modalContent.querySelector('.product__title');
        const details = this.modalContent.querySelector('.product__view-details');
        if (info && title && details) title.after(details);

        // The add-to-cart pill is pinned to the bottom of the scroll area so a
        // product with two option rows never hides it. position: sticky is
        // bounded by its containing block, and main-product wraps <product-form>
        // in a bare div that is exactly as tall as the form — zero travel — so
        // the class goes on that wrapper, whose containing block is the whole
        // info column.
        const form = this.modalContent.querySelector('product-form');
        const buyBox = form && form.parentElement !== info ? form.parentElement : form;
        if (buyBox) buyBox.classList.add('cm-qv__buy');

        this.modalContent.querySelectorAll('.cm-qv__thumbs').forEach((old) => old.remove());

        const items = this.modalContent.querySelectorAll('.product__media-item');
        const primary = this.modalContent.querySelector('.product__media-item img');
        const wrapper = this.modalContent.querySelector('.product__media-wrapper');
        if (items.length < 2 || !primary || !wrapper) return;

        const shots = [];
        items.forEach((item) => {
          const img = item.querySelector('img');
          if (img && img.getAttribute('src')) {
            shots.push({ src: img.getAttribute('src'), srcset: img.getAttribute('srcset') || '', alt: img.alt || '' });
          }
        });
        if (shots.length < 2) return;

        const row = document.createElement('ul');
        row.className = 'cm-qv__thumbs';
        shots.slice(0, 5).forEach((shot, index) => {
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cm-qv__thumb';
          button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          button.setAttribute('aria-label', `Photo ${index + 1}`);
          const thumb = document.createElement('img');
          thumb.src = shot.src;
          thumb.alt = '';
          thumb.loading = 'lazy';
          thumb.decoding = 'async';
          button.appendChild(thumb);
          button.addEventListener('click', () => {
            primary.setAttribute('src', shot.src);
            if (shot.srcset) primary.setAttribute('srcset', shot.srcset);
            primary.alt = shot.alt;
            row.querySelectorAll('.cm-qv__thumb').forEach((other) => {
              other.setAttribute('aria-current', other === button ? 'true' : 'false');
            });
          });
          li.appendChild(button);
          row.appendChild(li);
        });
        wrapper.appendChild(row);
      }

      /* `data-product-url` may carry a variant — v2.2. A card names the plain
         product URL, but cm-cards.js's shared opener passes through whatever
         the [data-cm-qv-open] element asked for, and the cart drawer's
         "Change" link asks for `line_item.url`, i.e. `?variant=<id>`. Nothing
         here has to interpret it: the id stays in the string, CMQuick keys the
         cache on it and forwards it to Shopify, and the section comes back
         with that variant's pills already checked — the same request shape
         product-info.js makes when a visitor picks an option on the product
         page. So the panel opens on the size and colour that are in the bag
         without a pill being clicked client-side, and without the re-fetch
         that clicking one would trigger. */
      show(opener) {
        const url = opener.getAttribute('data-product-url');
        const cache = window.CMQuick;
        const warm = cache ? cache.peek(url) : null;
        const token = (this.openToken += 1);

        // Warm: paint before the modal is even opened, so the first frame the
        // panel is visible already has the product in it.
        if (warm) this.fill(warm);
        else this.renderSkeleton();

        super.show(opener);

        // Desktop keeps its scroll: the panel is 420px in the corner, and
        // locking the whole document for it is what made the old modal feel
        // like it "took up a big part of the page".
        if (window.matchMedia('(min-width: 750px)').matches) {
          document.body.classList.remove('overflow-hidden');
        }

        if (warm) return;

        const pending = cache
          ? cache.get(url)
          : fetch(url)
              .then((response) => response.text())
              .then((text) => {
                const doc = new DOMParser().parseFromString(text, 'text/html');
                const el = doc.querySelector('product-info');
                return el ? el.outerHTML : null;
              });

        pending
          .then((html) => {
            if (token !== this.openToken || !this.hasAttribute('open')) return;
            this.fill(html);
          })
          .catch(() => {
            /* The panel keeps its skeleton; the card's own link still works. */
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
